import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, CheckCircle2, KeyRound } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ActivatePage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!token) { setError('Missing activation token.'); setLoading(false); return; }
      try {
        const res = await axios.get(`${API}/auth/activation/${token}`);
        setInfo(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'This activation link is invalid or expired.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { toast.error('Passwords do not match.'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/activation/${token}`, { password });
      if (res.data?.token) {
        localStorage.setItem('token', res.data.token);
        localStorage.setItem('user', JSON.stringify(res.data.user));
        toast.success('Account activated! Loading your onboarding…');
        setTimeout(() => { window.location.href = '/my-profile?tab=onboarding'; }, 600);
      } else {
        toast.error(res.data?.message || 'Activation failed.');
        setSubmitting(false);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Activation failed.');
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="activate-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
        <div className="flex items-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center">
            <KeyRound className="text-white" size={20} />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-slate-900">Activate your account</h1>
            <p className="text-xs text-slate-500">Set a password to start onboarding</p>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-10 text-slate-400" data-testid="activate-loading">
            <Loader2 className="animate-spin" size={22} />
          </div>
        )}

        {!loading && error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="activate-error">
            {error}
          </div>
        )}

        {!loading && !error && info && (
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="activate-form">
            <div className="text-sm text-slate-600">
              Welcome{info.name ? `, ${info.name}` : ''}. Your account
              {info.email ? <> (<span className="font-medium">{info.email}</span>)</> : ''} is ready.
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">New password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="At least 8 characters" data-testid="activate-password" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Confirm password</label>
              <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Re-enter password" data-testid="activate-confirm" />
            </div>
            <button type="submit" disabled={submitting}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-lg py-2.5 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="activate-submit">
              {submitting ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />}
              {submitting ? 'Activating…' : 'Activate & Continue'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
